<template>
    <Card class="mb-4">
        <CardContent class="p-4">
            <!-- Header Row -->
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-4">
                    <img :src="logo" :alt="`${title} logo`" class="w-10 h-10" />
                    <div>
                        <div class="flex items-center gap-2">
                            <span class="text-lg font-medium">{{ title }}</span>
                            <CheckCircle v-if="isConnected" class="h-5 w-5 text-accent-foreground" />
                        </div>
                        <div class="flex items-center gap-2 text-sm text-muted-foreground">
                            <Zap class="h-4 w-4" />
                            <span>Webhook</span>
                            <Button
                                v-if="webhook && !showWebhook"
                                variant="ghost"
                                size="sm"
                                class="h-auto p-1"
                                @click="copyToClipboard(webhook)"
                                :title="isCopying ? 'Copied!' : 'Copy'">
                                <Check v-if="isCopying" class="h-4 w-4" />
                                <Clipboard v-else class="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
                <!-- Action Button in header row -->
                <div class="flex items-center gap-2">
                    <LoaderCircle v-if="loading" class="h-4 w-4 animate-spin" />
                    <Button
                        @click="toggleConnection"
                        :disabled="loading"
                        :variant="isConnected ? 'outline' : 'default'"
                        :class="isConnected ? 'border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground' : ''">
                        {{ isConnected ? 'Disconnect' : 'Connect' }}
                    </Button>
                </div>
            </div>
            <!-- Secondary Row: Configuration Input -->
            <div v-if="showWebhook" class="mt-4 pt-4 border-t space-y-4">
                <div class="relative">
                    <Input
                        v-model="inputValue"
                        :type="show ? 'text' : 'password'"
                        :placeholder="placeholder || 'Enter webhook URL'"
                        :disabled="loading"
                        class="pr-10" />
                    <Button
                        variant="ghost"
                        size="sm"
                        class="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                        @click="show = !show">
                        <EyeOff v-if="show" class="h-4 w-4" />
                        <Eye v-else class="h-4 w-4" />
                    </Button>
                </div>
                <div class="flex justify-end gap-2">
                    <Button
                        variant="outline"
                        @click="cancelInput"
                        :disabled="loading">
                        Cancel
                    </Button>
                    <Button
                        @click="saveInput"
                        :disabled="loading || inputValue === ''">
                        <LoaderCircle v-if="loading" class="h-4 w-4 animate-spin mr-2" />
                        Save
                    </Button>
                </div>
            </div>
        </CardContent>
    </Card>
</template>

<script setup>
import { ref } from 'vue';
import { CheckCircle, Eye, EyeOff, Clipboard, Check, Zap, LoaderCircle } from 'lucide-vue-next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import * as notificationsApi from '../../../api/notifications.api';

const props = defineProps({
    title: { type: String, required: true },
    logo: { type: String, required: true },
    isConnected: { type: Boolean, default: false },
    placeholder: { type: String, default: null },
    webhook: { type: String, default: null },
    subscriberId: { type: String, default: null },
    providerId: { type: String, default: null }
});

const emit = defineEmits(['update:webhook', 'update:isConnected']);

const showWebhook = ref(false);
const loading = ref(false);
const inputValue = ref(props.webhook || '');
const show = ref(false);
const isCopying = ref(false);

const copyToClipboard = (textToCopy) => {
    navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
            isCopying.value = true;
            setTimeout(() => {
                isCopying.value = false;
            }, 1000);
        })
        .catch((err) => {
            console.error('Could not copy text: ', err);
        });
};

const toggleConnection = async () => {
    loading.value = true;
    try {
        const connected = !props.isConnected;
        if (connected) {
            showWebhook.value = true;
        } else {
            await notificationsApi.deleteSubscriberCredentials(props.subscriberId, props.providerId);
            emit('update:webhook', null);
            inputValue.value = '';
            showWebhook.value = false;
            emit('update:isConnected', false);
        }
    } catch (error) {
        console.error('Error toggling webhook connection:', error);
    } finally {
        loading.value = false;
    }
};

const saveInput = async () => {
    loading.value = true;
    try {
        await notificationsApi.updateSubscriberCredentials(props.subscriberId, props.providerId, inputValue.value);
        emit('update:webhook', inputValue.value);
        emit('update:isConnected', true);
        showWebhook.value = false;
    } catch (error) {
        console.error('Failed to update subscriber:', error);
    } finally {
        loading.value = false;
    }
};

const cancelInput = () => {
    inputValue.value = props.webhook || '';
    emit('update:isConnected', false);
    showWebhook.value = false;
};
</script>

